'use client';
import React, { useState, useEffect } from 'react';
import { X, Plus, Eye } from 'lucide-react';

interface Status {
  id: string;
  userId: string;
  userName: string;
  userAvatar: string;
  content: string;
  type: 'text' | 'image';
  imageUrl?: string;
  bgColor?: string;
  createdAt: number; // timestamp ms
  views: number;
  isOwn?: boolean;
}

const THIRTY_SIX_HOURS = 36 * 60 * 60 * 1000;

const SAMPLE_STATUSES: Status[] = [
{ id: 's1', userId: 'u1', userName: 'Amina K.', userAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1674fe127-1767165224694.png", content: 'Just listed my iPhone 15 Pro on China Ndogo! 📱', type: 'text', bgColor: '#1a1a2e', createdAt: Date.now() - 2 * 3600000, views: 24, isOwn: false },
{ id: 's2', userId: 'u2', userName: 'Peter M.', userAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_14f1c9438-1772346294870.png", content: 'New job opening at my company! Check Jobs section 💼', type: 'text', bgColor: '#0d2137', createdAt: Date.now() - 5 * 3600000, views: 41, isOwn: false },
{ id: 's3', userId: 'u3', userName: 'Grace W.', userAvatar: "https://images.unsplash.com/photo-1721613883249-6242e2effbf3", content: 'Beautiful apartment in Westlands available! 🏠', type: 'text', bgColor: '#1a2e1a', createdAt: Date.now() - 8 * 3600000, views: 18, isOwn: false },
{ id: 's4', userId: 'me', userName: 'My Status', userAvatar: "https://images.unsplash.com/photo-1692220713225-8bc4f42ba14a", content: 'Tap to add your status', type: 'text', bgColor: '#1a1a1a', createdAt: Date.now() - 1 * 3600000, views: 0, isOwn: true }];


function timeAgo(ts: number): string {
  const diff = Date.now() - ts;
  const h = Math.floor(diff / 3600000);
  const m = Math.floor(diff % 3600000 / 60000);
  if (h > 0) return `${h}h ago`;
  return `${m}m ago`;
}

function timeLeft(ts: number): string {
  const remaining = THIRTY_SIX_HOURS - (Date.now() - ts);
  if (remaining <= 0) return 'Expired';
  const h = Math.floor(remaining / 3600000);
  return `${h}h left`;
}

export default function StatusBar() {
  const [statuses, setStatuses] = useState<Status[]>(() =>
  SAMPLE_STATUSES.filter((s) => Date.now() - s.createdAt < THIRTY_SIX_HOURS)
  );
  const [open, setOpen] = useState(false);
  const [viewing, setViewing] = useState<Status | null>(null);
  const [viewIdx, setViewIdx] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  const [newText, setNewText] = useState('');
  const [newBg, setNewBg] = useState('#1a1a2e');
  const [progress, setProgress] = useState(0);

  // Auto-delete expired statuses
  useEffect(() => {
    const interval = setInterval(() => {
      setStatuses((prev) => prev.filter((s) => Date.now() - s.createdAt < THIRTY_SIX_HOURS));
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Progress bar for viewing
  useEffect(() => {
    if (!viewing) return;
    setProgress(0);
    const interval = setInterval(() => {
      setProgress((p) => {
        if (p >= 100) {
          // Auto advance
          const others = statuses.filter((s) => !s.isOwn);
          const idx = others.findIndex((s) => s.id === viewing.id);
          if (idx < others.length - 1) {
            setViewing(others[idx + 1]);
            setViewIdx(idx + 1);
          } else {
            setViewing(null);
          }
          return 0;
        }
        return p + 2;
      });
    }, 100);
    return () => clearInterval(interval);
  }, [viewing, statuses]);

  const handleAddStatus = () => {
    if (!newText.trim()) return;
    const newStatus: Status = {
      id: `s-${Date.now()}`, userId: 'me', userName: 'My Status',
      userAvatar: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=80&q=80',
      content: newText, type: 'text', bgColor: newBg,
      createdAt: Date.now(), views: 0, isOwn: true
    };
    setStatuses((prev) => [newStatus, ...prev.filter((s) => !s.isOwn)]);
    setNewText('');
    setAddOpen(false);
  };

  const activeStatuses = statuses.filter((s) => !s.isOwn);
  const myStatus = statuses.find((s) => s.isOwn);

  return (
    <>
      {/* Status Bubble — top right corner */}
      <button
        onClick={() => setOpen(true)}
        className="fixed top-4 right-4 z-40 flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all active:scale-95"
        style={{ background: 'var(--card)', border: '1.5px solid var(--primary)', boxShadow: '0 2px 12px rgba(0,200,83,0.25)' }}
        aria-label="View statuses">
        
        <div className="flex -space-x-1.5">
          {activeStatuses.slice(0, 3).map((s) =>
          <img key={s.id} src={s.userAvatar} alt={s.userName} className="w-5 h-5 rounded-full border border-card object-cover" />
          )}
        </div>
        <span className="text-[10px] font-700" style={{ color: 'var(--primary)' }}>
          {activeStatuses.length > 0 ? `${activeStatuses.length}` : '+'}
        </span>
      </button>

      {/* Status Panel */}
      {open &&
      <div className="fixed inset-0 z-50 flex flex-col" style={{ background: 'var(--background)' }}>
          {/* Header */}
          <div className="flex items-center justify-between px-5 pt-5 pb-4" style={{ borderBottom: '1px solid var(--border)' }}>
            <h2 className="text-lg font-800 text-foreground">Status</h2>
            <button onClick={() => setOpen(false)} className="p-2 rounded-xl" style={{ background: 'var(--secondary)' }}>
              <X size={18} style={{ color: 'var(--foreground)' }} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-4">
            {/* My Status */}
            <div className="mb-4">
              <p className="text-xs font-700 mb-2" style={{ color: 'var(--muted-foreground)' }}>MY STATUS</p>
              <button onClick={() => setAddOpen(true)}
            className="flex items-center gap-3 w-full p-3 rounded-2xl"
            style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
                <div className="relative">
                  <img src="https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=80&q=80" alt="Me" className="w-12 h-12 rounded-full object-cover" />
                  <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: 'var(--primary)' }}>
                    <Plus size={12} style={{ color: '#001A08' }} />
                  </div>
                </div>
                <div className="text-left">
                  <p className="text-sm font-700 text-foreground">Add to my status</p>
                  <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                    {myStatus ? `Last updated ${timeAgo(myStatus.createdAt)} · ${timeLeft(myStatus.createdAt)}` : 'Tap to add a status update'}
                  </p>
                </div>
              </button>
            </div>

            {/* Recent Updates */}
            {activeStatuses.length > 0 &&
          <div>
                <p className="text-xs font-700 mb-2" style={{ color: 'var(--muted-foreground)' }}>RECENT UPDATES</p>
                <div className="flex flex-col gap-2">
                  {activeStatuses.map((s, idx) =>
              <button key={s.id} onClick={() => {setViewing(s);setViewIdx(idx);setOpen(false);}}
              className="flex items-center gap-3 p-3 rounded-2xl text-left"
              style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
                      <div className="relative">
                        <img src={s.userAvatar} alt={s.userName} className="w-12 h-12 rounded-full object-cover" />
                        <div className="absolute inset-0 rounded-full" style={{ border: '2.5px solid var(--primary)', margin: '-2px' }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-700 text-foreground">{s.userName}</p>
                        <p className="text-xs truncate" style={{ color: 'var(--muted-foreground)' }}>{s.content}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[10px]" style={{ color: 'var(--muted-foreground)' }}>{timeAgo(s.createdAt)}</span>
                          <span className="text-[10px]" style={{ color: 'var(--muted-foreground)' }}>·</span>
                          <span className="text-[10px]" style={{ color: '#FFB300' }}>{timeLeft(s.createdAt)}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 text-[10px]" style={{ color: 'var(--muted-foreground)' }}>
                        <Eye size={10} />{s.views}
                      </div>
                    </button>
              )}
                </div>
              </div>
          }
          </div>
        </div>
      }

      {/* Status Viewer */}
      {viewing &&
      <div className="fixed inset-0 z-50 flex flex-col" style={{ background: viewing.bgColor || '#000' }}>
          {/* Progress bar */}
          <div className="flex gap-1 px-3 pt-3">
            {activeStatuses.map((s, i) =>
          <div key={s.id} className="flex-1 h-0.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.3)' }}>
                <div className="h-full rounded-full transition-all" style={{ background: '#fff', width: i < viewIdx ? '100%' : i === viewIdx ? `${progress}%` : '0%' }} />
              </div>
          )}
          </div>

          {/* Header */}
          <div className="flex items-center gap-3 px-4 pt-3 pb-2">
            <img src={viewing.userAvatar} alt={viewing.userName} className="w-10 h-10 rounded-full object-cover border-2 border-white/40" />
            <div className="flex-1">
              <p className="text-sm font-700 text-white">{viewing.userName}</p>
              <p className="text-xs text-white/60">{timeAgo(viewing.createdAt)} · {timeLeft(viewing.createdAt)}</p>
            </div>
            <button onClick={() => setViewing(null)} className="p-2">
              <X size={20} style={{ color: 'rgba(255,255,255,0.8)' }} />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 flex items-center justify-center px-8">
            {viewing.type === 'image' && viewing.imageUrl ?
          <img src={viewing.imageUrl} alt="Status" className="w-full rounded-2xl object-cover max-h-96" /> :

          <p className="text-2xl font-700 text-white text-center leading-relaxed">{viewing.content}</p>
          }
          </div>

          {/* Navigation areas */}
          <div className="absolute inset-0 flex" style={{ top: '80px', bottom: '80px' }}>
            <div className="flex-1" onClick={() => {
            if (viewIdx > 0) {setViewing(activeStatuses[viewIdx - 1]);setViewIdx(viewIdx - 1);}
          }} />
            <div className="flex-1" onClick={() => {
            if (viewIdx < activeStatuses.length - 1) {setViewing(activeStatuses[viewIdx + 1]);setViewIdx(viewIdx + 1);} else
            setViewing(null);
          }} />
          </div>

          {/* Bottom */}
          <div className="px-4 pb-6 pt-2 flex items-center gap-3">
            <div className="flex-1 px-4 py-3 rounded-full text-sm text-white/60" style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)' }}>
              Reply to {viewing.userName}...
            </div>
            <div className="flex items-center gap-1 text-white/60 text-xs">
              <Eye size={14} />{viewing.views}
            </div>
          </div>
        </div>
      }

      {/* Add Status Modal */}
      {addOpen &&
      <div className="fixed inset-0 z-50 flex items-end justify-center">
          <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.7)' }} onClick={() => setAddOpen(false)} />
          <div className="relative w-full max-w-lg rounded-t-3xl p-6" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-700 text-foreground">Add Status</h3>
              <button onClick={() => setAddOpen(false)}><X size={18} style={{ color: 'var(--muted-foreground)' }} /></button>
            </div>
            <div className="mb-3 p-4 rounded-2xl text-center" style={{ background: newBg, minHeight: '100px' }}>
              <p className="text-white text-lg font-600">{newText || 'Type your status...'}</p>
            </div>
            <textarea value={newText} onChange={(e) => setNewText(e.target.value)} placeholder="What's on your mind?"
          rows={3} maxLength={200}
          className="w-full px-4 py-3 rounded-xl text-sm outline-none resize-none mb-3"
          style={{ background: 'var(--input)', border: '1px solid var(--border)', color: 'var(--foreground)' }} />
            <div className="flex gap-2 mb-4">
              {['#1a1a2e', '#0d2137', '#1a2e1a', '#2e1a1a', '#2e2a1a', '#1a1a1a'].map((c) =>
            <button key={c} onClick={() => setNewBg(c)} className="w-8 h-8 rounded-full border-2" style={{ background: c, borderColor: newBg === c ? 'var(--primary)' : 'transparent' }} />
            )}
            </div>
            <p className="text-xs mb-3" style={{ color: 'var(--muted-foreground)' }}>⏱ Status auto-deletes after 36 hours</p>
            <button onClick={handleAddStatus} disabled={!newText.trim()}
          className="w-full py-3 rounded-2xl text-sm font-700 disabled:opacity-50"
          style={{ background: 'var(--primary)', color: '#001A08' }}>
              Post Status
            </button>
          </div>
        </div>
      }
    </>);

}